import { useCallback, useEffect, useRef } from 'react';

const useAutoSave = (sessionId, reviews, timeTracking) => {
    const saveTimeoutRef = useRef(null);
    const storageKey = `teacherReview_${sessionId}`;

    // Save to localStorage
    const saveToLocal = useCallback((data) => {
        try {
            const saveData = {
                sessionId,
                reviews: data?.reviews || reviews,
                timeTracking: data?.timeTracking || timeTracking,
                timestamp: new Date().toISOString()
            };
            localStorage.setItem(storageKey, JSON.stringify(saveData));
            return true;
        } catch (error) {
            console.error('Error saving to localStorage:', error);
            return false;
        }
    }, [sessionId, reviews, timeTracking, storageKey]);

    // Load from localStorage
    const loadFromLocal = useCallback(() => {
        try {
            const saved = localStorage.getItem(storageKey);
            if (saved) {
                const data = JSON.parse(saved);
                
                // Check if data is not too old (24 hours)
                const savedTime = new Date(data.timestamp);
                const now = new Date();
                const hoursDiff = (now - savedTime) / (1000 * 60 * 60);
                
                if (hoursDiff < 24 && data.sessionId === sessionId) {
                    return {
                        reviews: data.reviews || {},
                        timeTracking: data.timeTracking || {}
                    };
                } else {
                    // Clear old data
                    localStorage.removeItem(storageKey);
                }
            }
        } catch (error) {
            console.error('Error loading from localStorage:', error);
        }
        return null;
    }, [sessionId, storageKey]);

    // Clear saved data
    const clearLocal = useCallback(() => {
        try {
            localStorage.removeItem(storageKey);
        } catch (error) {
            console.error('Error clearing localStorage:', error);
        }
    }, [storageKey]);

    // Auto-save when reviews or timeTracking change
    useEffect(() => {
        // Clear any existing timeout
        if (saveTimeoutRef.current) {
            clearTimeout(saveTimeoutRef.current);
        }

        // Set new timeout for auto-save (debounced to 5 seconds)
        if ((reviews && Object.keys(reviews).length > 0) || (timeTracking && Object.keys(timeTracking).length > 0)) {
            saveTimeoutRef.current = setTimeout(() => {
                saveToLocal({ reviews, timeTracking });
            }, 5000);
        }

        // Cleanup
        return () => {
            if (saveTimeoutRef.current) {
                clearTimeout(saveTimeoutRef.current);
            }
        };
    }, [reviews, timeTracking, saveToLocal]);

    // Save immediately when window is about to close
    useEffect(() => {
        const handleBeforeUnload = (e) => {
            if ((reviews && Object.keys(reviews).length > 0) || (timeTracking && Object.keys(timeTracking).length > 0)) {
                saveToLocal({ reviews, timeTracking });
            }
        };

        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, [reviews, timeTracking, saveToLocal]);

    // Clean up old saved sessions periodically
    useEffect(() => {
        const cleanupOldSessions = () => {
            try {
                const keys = Object.keys(localStorage);
                const now = new Date();
                
                keys.forEach(key => {
                    if (key.startsWith('teacherReview_') && key !== storageKey) {
                        try {
                            const data = JSON.parse(localStorage.getItem(key));
                            const savedTime = new Date(data.timestamp);
                            const daysDiff = (now - savedTime) / (1000 * 60 * 60 * 24);
                            
                            // Remove if older than 7 days
                            if (daysDiff > 7) {
                                localStorage.removeItem(key);
                            }
                        } catch {
                            // Remove corrupted data
                            localStorage.removeItem(key);
                        }
                    }
                });
            } catch (error) {
                console.error('Error cleaning up old sessions:', error);
            }
        };

        // Run cleanup on mount
        cleanupOldSessions();
    }, [storageKey]);

    return {
        saveToLocal,
        loadFromLocal,
        clearLocal
    };
};

export default useAutoSave;